import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";

// shadcn/ui components
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "components/ui/pagination";
import { Card } from "components/ui/card";

// Lucide icons
import { CheckCircle2, User } from "lucide-react";
import { cn } from "lib/utils";

const EmpList = ({ rows, completed, totalRequests }) => {
  const [page, setPage] = useState(1);
  const pageSize = 24; // Good number for grid layout

  const totalPages = useMemo(() => {
    if (!rows || rows.length === 0) return 1;
    return Math.ceil(rows.length / pageSize);
  }, [rows, pageSize]);

  const paginatedRows = useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return rows.slice(start, end);
  }, [rows, page, pageSize]);

  const goToPage = (newPage) => {
    if (!newPage || newPage === page || newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const paginationItems = useMemo(() => {
    const pages = Number(totalPages) || 0;
    const current = Number(page) || 1;
    if (pages <= 1) return [];

    const items = [];
    const addPage = (p) => items.push({ type: "page", page: p, active: p === current });
    const addEllipsis = (key) => items.push({ type: "ellipsis", key });

    addPage(1);
    const start = Math.max(2, current - 1);
    const end = Math.min(pages - 1, current + 1);

    if (start > 2) addEllipsis("start");
    for (let p = start; p <= end; p += 1) addPage(p);
    if (end < pages - 1) addEllipsis("end");
    if (pages > 1) addPage(pages);
    return items;
  }, [page, totalPages]);

  const isCompleted = (employeeId) => {
    return completed?.find((c) => c === employeeId) !== undefined;
  };

  if (!rows || rows.length === 0) {
    return (
      <div className="py-12 text-center">
        <User className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
        <p className="text-sm font-medium text-muted-foreground">No members found</p>
        <p className="text-xs text-muted-foreground mt-1">
          No members are included in this survey
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
        <span className="font-medium">
          Total Members: <span className="text-foreground font-semibold">{rows.length}</span>
        </span>
        {completed && (
          <span className="font-medium">
            Completed: <span className="text-green-600 font-semibold">{completed.length}</span>
          </span>
        )}
        {totalRequests && (
          <span className="font-medium">
            Total Requests: <span className="text-foreground font-semibold">{totalRequests}</span>
          </span>
        )}
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
        {paginatedRows.map((employeeId, index) => {
          const isEmployeeCompleted = isCompleted(employeeId);
          return (
            <Link
              key={`${employeeId}-${index}`}
              to={`/app/employees/details/${employeeId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <Card
                className={cn(
                  "p-4 text-center transition-all duration-200 hover:shadow-md cursor-pointer border-2",
                  isEmployeeCompleted
                    ? "bg-green-50 border-green-200 hover:bg-green-100 hover:border-green-300"
                    : "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
                )}
              >
                <div className="flex flex-col items-center gap-2">
                  {isEmployeeCompleted && (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  )}
                  <span
                    className={cn(
                      "font-mono text-sm font-semibold",
                      isEmployeeCompleted ? "text-green-700" : "text-gray-600"
                    )}
                  >
                    {employeeId}
                  </span>
                </div>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <>
          <div className="flex items-center justify-center pt-4">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      goToPage(page > 1 ? page - 1 : 1);
                    }}
                    className={page === 1 ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
                {paginationItems.map((item, idx) =>
                  item.type === "ellipsis" ? (
                    <PaginationItem key={`ellipsis-${item.key}-${idx}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={`page-${item.page}`}>
                      <PaginationLink
                        href="#"
                        isActive={item.active}
                        onClick={(e) => {
                          e.preventDefault();
                          goToPage(item.page);
                        }}
                      >
                        {item.page}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      goToPage(page < totalPages ? page + 1 : totalPages);
                    }}
                    className={page >= totalPages ? "pointer-events-none opacity-50" : ""}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>

          {/* Page Info */}
          <div className="text-center text-sm text-muted-foreground">
            Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, rows.length)} of {rows.length} members
          </div>
        </>
      )}
    </div>
  );
};

export default EmpList;
